/**
 * Create Board Form
 *
 * Form for creating a new board from scratch with custom columns.
 * Replaces template-based board creation with flexible column builder.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AsyncButton } from '@/components/ui';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import { sizing, radius, spacing } from '@/styles/design-tokens';
import { ColumnBuilder } from './ColumnBuilder';
import { CreateBoardColumnsItem } from '@/types/generated/processor-types';
import { Action } from '@/business/types/action.types';
import type { CreateBoardInput, CreateBoardOutput } from '@/types/generated';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';

import { useAuth } from '@/hooks/useAuth';
import { useUserGroupsQuery } from '@/hooks/queries';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { getDefaultColumnColors } from '@/business/constants/column-colors';

interface CreateBoardFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (boardId: string) => void;
}

export default function CreateBoardForm({
  isOpen,
  onClose,
  onSuccess,
}: CreateBoardFormProps) {
  const colors = useThemeColors();
  const defaultColumnColors = getDefaultColumnColors(colors);
  const defaultColumns = useMemo<CreateBoardColumnsItem[]>(
    () => [
      { name: 'To Do', position: 0, color: defaultColumnColors.TODO },
      {
        name: 'In Progress',
        position: 1,
        color: defaultColumnColors.IN_PROGRESS,
      },
      { name: 'Done', position: 2, color: defaultColumnColors.DONE },
    ],
    [
      defaultColumnColors.TODO,
      defaultColumnColors.IN_PROGRESS,
      defaultColumnColors.DONE,
    ]
  );
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const [boardTitle, setBoardTitle] = useState('');
  const [isGroupBoard, setIsGroupBoard] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [columns, setColumns] =
    useState<CreateBoardColumnsItem[]>(defaultColumns);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { execute: executeCreateBoard, isLoading: isCreating } =
    useOrchestrator<CreateBoardInput, CreateBoardOutput>(Action.CREATE_BOARD);

  const { data: groupsData, isLoading: isLoadingGroups } = useUserGroupsQuery(
    isGroupBoard ? activeOrganizationId : undefined
  );
  const groups = groupsData?.groups ?? [];

  const handleColumnsChange = (newColumns: CreateBoardColumnsItem[]) => {
    setColumns(newColumns);
  };

  // Reset form state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setBoardTitle('');
      setIsGroupBoard(false);
      setSelectedGroupId('');
      setColumns(defaultColumns);
      setErrors({});
    }
  }, [isOpen, defaultColumns]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate board title
    const trimmedTitle = boardTitle.trim();
    if (trimmedTitle.length === 0) {
      newErrors.boardTitle = 'Board title is required';
    } else if (trimmedTitle.length > 100) {
      newErrors.boardTitle = 'Board title too long (max 100 characters)';
    }

    // Validate columns
    if (columns.length === 0) {
      newErrors.columns = 'At least one column is required';
    } else if (columns.length > 20) {
      newErrors.columns = 'Too many columns (max 20)';
    }

    columns.forEach((col, i) => {
      if (col.name.trim().length === 0) {
        newErrors[`column_${i}`] = `Column ${i + 1} name cannot be empty`;
      } else if (col.name.length > 50) {
        newErrors[`column_${i}`] =
          `Column ${i + 1} name too long (max 50 chars)`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [boardTitle, columns]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!validateForm()) {
        return;
      }

      // Check user authentication
      if (!user || !user.organization_id) {
        setErrors({ submit: 'User not authenticated or missing organization' });
        return;
      }

      // organization_id is auto-injected by ActionOrchestrator before validation
      const requestData: Omit<CreateBoardInput, 'organization_id'> = {
        board_title: boardTitle.trim(),
        group_id: isGroupBoard ? selectedGroupId : undefined,
        owner_id: user.id,
        columns: columns.map((col, i) => ({
          name: col.name.trim(),
          position: i,
          color: col.color,
          config: col.config || {},
        })),
      };

      const result = await executeCreateBoard(requestData as CreateBoardInput);

      if (result.code === ResponseCode.SUCCESS && result.data) {
        onSuccess(result.data.board_id);
        onClose();
        setBoardTitle('');
        setIsGroupBoard(false);
        setSelectedGroupId('');
        setColumns(defaultColumns);
        setErrors({});
      }
    },
    [
      boardTitle,
      isGroupBoard,
      selectedGroupId,
      columns,
      user,
      onSuccess,
      onClose,
      validateForm,
      defaultColumns,
      executeCreateBoard,
    ]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isCreating) {
      onClose();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent Enter key from submitting the form
    // Users must click the "Create Board" button explicitly
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const isValid =
    boardTitle.trim().length > 0 &&
    boardTitle.trim().length <= 100 &&
    columns.length > 0 &&
    columns.every(col => col.name.trim().length > 0 && col.name.length <= 50) &&
    (!isGroupBoard || !!selectedGroupId);

  return (
    <Dialog open={isOpen} onOpenChange={isCreating ? undefined : onClose}>
      <DialogContent
        className="max-w-4xl flex flex-col"
        style={{ maxHeight: '90vh' }}
        onKeyDown={handleKeyDown}
      >
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-2 -mx-2">
            <div className="space-y-4">
              {/* Header */}
              <div>
                <h3
                  className="font-semibold text-lg mb-2"
                  style={{ color: colors.text.primary }}
                >
                  Create New Board
                </h3>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  Create a board from scratch with custom columns
                </p>
              </div>

              {/* Board Title Input */}
              <div>
                <label
                  htmlFor="board-title"
                  className="block text-sm font-medium mb-2"
                  style={{ color: colors.text.primary }}
                >
                  Board Title <span style={{ color: colors.error }}>*</span>
                </label>
                <input
                  id="board-title"
                  type="text"
                  value={boardTitle}
                  onChange={e => setBoardTitle(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  maxLength={100}
                  disabled={isCreating}
                  className="w-full px-3 transition-colors"
                  style={{
                    backgroundColor: colors.offWhite,
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: errors.boardTitle
                      ? colors.error
                      : colors.border.default,
                    color: colors.text.primary,
                    borderRadius: radius.md,
                    height: sizing.input.md,
                    outline: 'none',
                  }}
                  onFocus={e => {
                    if (!errors.boardTitle) {
                      e.currentTarget.style.borderColor = colors.primary;
                    }
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = errors.boardTitle
                      ? colors.error
                      : colors.border.default;
                  }}
                  placeholder="Enter board title"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs" style={{ color: colors.error }}>
                    {errors.boardTitle || ''}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: colors.text.secondary }}
                  >
                    {boardTitle.length}/100
                  </span>
                </div>
              </div>

              {/* Group Board Toggle */}
              <div>
                <div
                  className="flex items-center gap-3 border"
                  style={{
                    backgroundColor: colors.offWhite,
                    borderColor: colors.border.default,
                    borderRadius: radius.md,
                    padding: `${spacing.xs} ${spacing.sm}`,
                  }}
                >
                  <input
                    id="is-group-board"
                    type="checkbox"
                    checked={isGroupBoard}
                    onChange={e => setIsGroupBoard(e.target.checked)}
                    disabled={isCreating}
                    style={{
                      cursor: isCreating ? 'not-allowed' : 'pointer',
                      flexShrink: 0,
                    }}
                  />
                  <label
                    htmlFor="is-group-board"
                    className="text-sm font-medium flex-1"
                    style={{
                      color: colors.text.primary,
                      cursor: isCreating ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'calc(2px * var(--sizing-multiplier, 1))',
                    }}
                  >
                    <span>Group Board</span>
                    <span
                      className="text-xs font-normal"
                      style={{
                        color: colors.text.secondary,
                      }}
                    >
                      Group boards are shared with your organization
                    </span>
                  </label>
                </div>

                {/* Group Selector - Show only when Group Board is checked */}
                {isGroupBoard && (
                  <div className="mt-3">
                    <label
                      htmlFor="group-select"
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.text.primary }}
                    >
                      Select Group{' '}
                      <span style={{ color: colors.error }}>*</span>
                    </label>
                    {isLoadingGroups ? (
                      <div className="flex items-center gap-2 py-2">
                        <Icon
                          name={IconName.LOADER}
                          size="sm"
                          className="animate-spin"
                          style={{ color: colors.text.secondary }}
                        />
                        <span
                          className="text-sm"
                          style={{ color: colors.text.secondary }}
                        >
                          Loading groups...
                        </span>
                      </div>
                    ) : (
                      <select
                        id="group-select"
                        value={selectedGroupId}
                        onChange={e => setSelectedGroupId(e.target.value)}
                        disabled={isCreating}
                        className="w-full px-3"
                        style={{
                          backgroundColor: colors.offWhite,
                          borderWidth: '2px',
                          borderStyle: 'solid',
                          borderColor: colors.border.default,
                          color: selectedGroupId
                            ? colors.text.primary
                            : colors.text.secondary,
                          borderRadius: radius.md,
                          height: sizing.input.md,
                          outline: 'none',
                        }}
                      >
                        <option value="">Select a group...</option>
                        {groups.map(group => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>

              {/* Columns Section — shared WYSIWYG ColumnBuilder */}
              <ColumnBuilder
                columns={columns}
                onColumnsChange={handleColumnsChange}
                disabled={isCreating}
                error={errors.columns}
              />
            </div>
          </div>

          {/* Footer Buttons */}
          <div
            className="flex justify-between pt-4 border-t mt-4"
            style={{ borderColor: colors.border.default }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="px-4 py-2 font-medium"
              style={{
                backgroundColor: colors.background,
                color: colors.text.primary,
                border: `1px solid ${colors.border.default}`,
                borderRadius: radius.DEFAULT,
                height: sizing.button.md,
                opacity: isCreating ? 0.5 : 1,
                cursor: isCreating ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                if (!isCreating) {
                  e.currentTarget.style.backgroundColor = colors.offWhite;
                  e.currentTarget.style.borderColor = colors.text.secondary;
                }
              }}
              onMouseLeave={e => {
                if (!isCreating) {
                  e.currentTarget.style.backgroundColor = colors.background;
                  e.currentTarget.style.borderColor = colors.border.default;
                }
              }}
            >
              Cancel
            </button>
            <AsyncButton
              onClick={handleSubmit}
              disabled={!isValid}
              isLoading={isCreating}
              icon={IconName.PLUS}
              loadingText="Creating..."
              className="px-6"
            >
              Create Board
            </AsyncButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
